import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { BadgeCheck, CalendarClock, Headphones } from "lucide-react";
import { MaskLines, Eyebrow } from "@/components/site/Motion";
import { QuoteButton } from "@/components/site/Nav";
import { RotatingLogo } from "@/components/site/RotatingLogo";

const TiltCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div onMouseMove={onMove} onMouseLeave={reset} style={{ perspective: 1200 }} className="relative" data-testid="hero-tilt-card">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div initial={{ clipPath: "inset(0 0 100% 0 round 2rem)" }} animate={{ clipPath: "inset(0 0 0% 0 round 2rem)" }} transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
          className="relative overflow-hidden rounded-[2rem] shadow-[0_40px_120px_-30px_rgba(11,42,111,0.55)]">
          <img src="/img/hero.jpg" alt="Customer paying by card on a modern payment terminal" className="aspect-[4/5] w-full object-cover sm:aspect-square lg:aspect-[4/5]" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_45%,rgba(5,11,31,0.7)_100%)]" />
          <div className="absolute inset-x-6 bottom-6 text-white">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-lime-300">Transaction fees</p>
            <p className="font-display text-3xl font-extrabold">Eliminated.</p>
          </div>
        </motion.div>
        <motion.div style={{ transform: "translateZ(120px)" }} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="absolute right-8 top-8">
          <RotatingLogo size={110} testid="hero-rotating-logo" />
        </motion.div>
        <motion.div style={{ transform: "translateZ(80px)" }} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.2, duration: 0.8 }}
          className="absolute left-3 top-10 flex items-center gap-3 rounded-2xl bg-white p-4 shadow-2xl sm:-left-12">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#0B2A6F] text-lime-300"><Headphones className="h-5 w-5" /></span>
          <div>
            <p className="text-sm font-bold leading-tight text-[#050B1F]">Real people</p>
            <p className="text-xs text-slate-600">Dedicated account manager</p>
          </div>
        </motion.div>
        <motion.div style={{ transform: "translateZ(90px)" }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 0.8 }}
          className="absolute -bottom-6 right-3 flex items-center gap-3 rounded-2xl bg-lime-400 p-4 text-[#050B1F] shadow-2xl sm:-right-8">
          <BadgeCheck className="h-8 w-8" />
          <div>
            <p className="text-sm font-bold leading-tight">Free equipment</p>
            <p className="text-xs font-medium">for qualifying merchants</p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-[linear-gradient(180deg,#EAF3FF_0%,#FFFFFF_85%)] px-4 pb-24 pt-40 sm:px-6 lg:px-12 lg:pb-32 lg:pt-48" data-testid="hero-section">
      <div className="absolute -right-40 -top-20 h-[36rem] w-[36rem] rounded-full bg-lime-300/30 blur-3xl" />
      <div className="absolute -left-40 bottom-0 h-[28rem] w-[28rem] rounded-full bg-emerald-300/30 blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Eyebrow>Credit card processing</Eyebrow>
          </motion.div>
          <h1 className="mt-6 font-display text-[3.2rem] font-extrabold leading-[0.95] tracking-tight text-[#050B1F] sm:text-7xl lg:text-[6.2rem]" data-testid="hero-headline">
            <MaskLines lines={["Zero fees.", <span key="l" className="text-emerald-600">Real support.</span>]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-8 max-w-xl text-base text-slate-700 md:text-lg">
            A complete payment solution built around your business. We eliminate transaction fees, equip qualifying merchants for free, and back you with a dedicated account manager from day one.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }} className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
            <QuoteButton testid="hero-quote-btn" dark className="!px-8 !py-4 text-base">Get Started</QuoteButton>
            <a href="#book-call" data-testid="hero-book-call-link" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#0B2A6F]/25 px-7 py-4 text-sm font-bold text-[#0B2A6F] transition-colors hover:border-[#0B2A6F] hover:bg-white">
              <CalendarClock className="h-4 w-4" /> Book a call
            </a>
          </motion.div>
        </div>
        <motion.div style={{ y }} className="lg:col-span-5">
          <TiltCard />
        </motion.div>
      </div>
    </section>
  );
};
